import { Injectable } from '@angular/core';
import type { ClientEnvironmentConfig } from '@core/config-models';
import { analytics } from '@charon/analytics-js';
import { type RudderAnalytics } from '@rudderstack/analytics-js';

import { decorateRudderstack } from './decorate-rudderstack-calls';

@Injectable({ providedIn: 'root' })
export class JourneyAnalyticsLoader {
  /**
   * Loads an implementation of the JourneyAnalytics interface.
   *
   * This method does a dynamic import of either rudderstack or a stub of the service,
   * to provide developer feedback whilst running locally.
   * @param config Client configuration.
   */
  async load(config: ClientEnvironmentConfig) {
    let rudderstack: RudderAnalytics;

    if (config.journeyAnalytics.enabled) {
      rudderstack = new (
        await import('@rudderstack/analytics-js')
      ).RudderAnalytics();
    } else {
      rudderstack = (await import('./stub')).stub(config);
    }

    const { writeKey, cdnEndpoint, apiEndpoint, dataPlaneEndpoint } =
      config.journeyAnalytics;

    analytics.load(
      writeKey,
      dataPlaneEndpoint,
      {
        secureCookie: true,
        pluginsSDKBaseURL: `${cdnEndpoint}/v3/modern/plugins`,
        destSDKBaseURL: `${cdnEndpoint}/v3/modern/js-integrations`,
        configUrl: apiEndpoint,
        storage: {
          type: 'cookieStorage',
        },
        consentManagement: {
          enabled: true,
          provider: 'custom',
        },
        preConsent: {
          enabled: false,
        },
      },
      () => decorateRudderstack(rudderstack, config)
    );
  }

  /**
   * Reference to the analytics implementation that is loaded.
   * Either Rudderstack or a stub.
   */
  getInstance() {
    return analytics;
  }
}
